import React, { useState, useCallback, useMemo } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditCommentModal from 'components/admin/common/modal/Blog/EditCommentModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';

const BlogComment = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createCommentData,
        postError: creatingError,
        postLoading: creating,
        putData: updateCommentData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteCommentData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.BLOG_COMMENTS);

    const { fetchData: blogData } = useCrud(API_ENDPOINTS.BLOGS);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseDeleteComment = () => {
        setErrors({});
        clearServerErrors();
    };

    const handleCloseEditComment = () => {
        setErrors({});
        clearServerErrors();
    };

    const { isOpen: isOpenDeleteComment, modalData: commentToDelete, openModal: openDeleteComment, closeModal: closedDeleteComment } = useModalState({
        onClose: handleCloseDeleteComment
    });

    const { isOpen: isOpenEditComment, modalData: selectedComment, openModal: openEditComment, closeModal: closedEditComment } = useModalState({
        onClose: handleCloseEditComment
    });

    const validationRules = [
        { field: 'blog_id', method: 'required' },
        { field: 'image', method: ['required', 'imageFormat'] },
        { field: 'author_name', method: 'required' },
    ];

    const blogOptions = useMemo(() => {
        if (blogData?.length > 0) {
            return blogData.map(blog => ({
                label: blog.section_title,
                value: blog.id
            }));
        } else {
            return [{ label: 'a', value: '1' }, { label: 'a', value: '3' }];
        }
    }, [blogData]);

    const handleModalSave = useCallback(async (updatedComment) => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(updatedComment, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        setErrors({});
        try {
            if (updatedComment.id) {
                await updateCommentData(updatedComment.id, updatedComment, true);
                setSuccessMessage("Comentario actualizado con éxito.");
            } else {
                await createCommentData(updatedComment, true);
                setSuccessMessage("Comentario creado con éxito.");
            }
            closedEditComment();
            setErrors({});
            refetch();
        } catch (error) {
            let firstError = "Algo salió mal en la creación de los comentarios del Blog.";
            if (error.response && error.response.data && typeof error.response.data.errors === 'object') {
                firstError = Object.values(error.response.data.errors)[0][0] || firstError;
            }
            setErrors({ form: firstError });
        }
    }, [updateCommentData, createCommentData, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newComment = {
            blog_id: '',
            image: '',
            author_image: '',
            author_name: '',
            author_designation: '',
        };
        openEditComment(newComment);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (commentToDelete) {
            try {
                await deleteCommentData(commentToDelete.id);
                setSuccessMessage("Comentario eliminado con éxito.");
                refetch();
                closedDeleteComment();
            } catch (error) {
                const firstError = error.response.data.error;
                setErrors({ form: firstError });
            }
        }
    }, [commentToDelete, deleteCommentData, refetch]);

    const columns = useMemo(() => [
        { field: 'blog_id', header: 'ID del Blog', sortable: true },
        { field: 'blog_section_title', header: 'Nombre del Blog', sortable: true },
        { field: 'author_name', header: 'Titulo del comentario', sortable: true },
        { field: 'author_designation', header: 'Subtitulo del comentario', sortable: true },
    ], []);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Comentarios</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Comentario
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={openEditComment}
                onDelete={openDeleteComment}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="blog_id"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditCommentModal
                show={isOpenEditComment}
                comment={selectedComment}
                handleClose={closedEditComment}
                handleSave={handleModalSave}
                updating={updating || creating}
                errors={errors}
                requestError={creatingError || updatingError}
                blogOptions={blogOptions}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteComment}
                onHide={closedDeleteComment}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(BlogComment);